// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * What a conductor's live and inferred state adds to its paint, derived once
 * per conductor per poll and shared by the path, its flow overlay, the
 * breaker on it and the status dot of the device it feeds.
 *
 * Pure: consumed by `SldEdgeLayer`, `SldStage` and the tests, and by nothing
 * that renders on its own.
 */

import type { StatusToken } from '../../../../utils/entityHelpers';
import type { PowerFlowConductor } from '../../../../utils/powerFlowRouting';
import type { PowerFlowInference } from '../../../../utils/powerFlowInference';
import type { PowerFlowLiveState } from '../../../../utils/powerFlowLive';

export interface ConductorPaint {
  /** Live and inferred classes, `sld-edge--*`. */
  classes: string[];
  /** Carrying load: the flow overlay is drawn and the breaker passes it. */
  flowing: boolean;
}

/** A conductor takes the more serious of its two ends' statuses. */
export const conductorStatus = (
  conductor: PowerFlowConductor,
  statusOf: Map<string, StatusToken>
): StatusToken | null => {
  const from = statusOf.get(conductor.source);
  const to = statusOf.get(conductor.target);
  if (from === 'fault' || to === 'fault') return 'fault';
  if (from === 'warn' || to === 'warn') return 'warn';
  return null;
};

/**
 * The classes a conductor's live and inferred state add. Exported so the
 * mapping from state to paint is testable without rendering.
 *
 * One paint: every run is the energized red, whatever its branch or
 * whether a reading confirms it. The line shows one state, **off** — a
 * measured `dead`, an inferred `standby`, an idle bypass — as lower opacity in
 * the same red, classed apart so the legend can name which is which. `unknown`
 * adds nothing to the line; the detail panel and captions say what is and is
 * not measured.
 *
 * Flow is an overlay rather than a colour: the red conductor stays, and a
 * green running dash is drawn on top of it, so "energized" and "carrying
 * load" are two visible facts rather than one replaced by the other.
 */
export const conductorClasses = (
  conductor: PowerFlowConductor,
  live: PowerFlowLiveState | undefined,
  inference: PowerFlowInference | undefined,
  showInferred: boolean
): ConductorPaint => {
  const classes: string[] = [];
  if (conductor.isTie) return { classes, flowing: false };

  const energization = live?.energizedOf.get(conductor.id);
  if (energization === 'dead') classes.push('sld-edge--dead');

  // With the inferred tier off, nothing is claimed about movement — except
  // that a *measured* dead run is still off, and `--dead` alone has no paint:
  // `--idle` is what draws the dots.
  if (!showInferred) {
    if (energization === 'dead') classes.push('sld-edge--idle');
    return { classes, flowing: false };
  }

  if (energization === 'standby') classes.push('sld-edge--standby');
  if (inference?.bypassLinkIds.has(conductor.id)) classes.push('sld-edge--bypass');
  if (inference?.riserLinkIds.has(conductor.id)) classes.push('sld-edge--riser');

  const flowing = live?.flowingOf.get(conductor.id) === true && energization !== 'dead';
  // Carrying load is solid with the running dash; anything else is dotted,
  // the dash the device symbols use for "nothing moving". Only with the
  // inferred tier on — flow is an inference, so with it off nothing is claimed.
  classes.push(flowing ? 'sld-edge--flowing' : 'sld-edge--idle');
  return { classes, flowing };
};

/** Every conductor's paint, keyed by id. */
export const paintConductors = (
  conductors: readonly PowerFlowConductor[],
  live: PowerFlowLiveState | undefined,
  inference: PowerFlowInference | undefined,
  showInferred: boolean
): Map<string, ConductorPaint> =>
  new Map(
    conductors.map(conductor => [
      conductor.id,
      conductorClasses(conductor, live, inference, showInferred),
    ])
  );
